import {
  Box,
  Button,
  Divider,
  Drawer,
  Tooltip,
  Typography,
} from '@mui/material';
import BackgroundLetterAvatar from 'components/core/Avatar/Avatar';
import { useLogout } from 'components/core/WithLogout';
import { useAppNavigation } from 'contexts/navigation';
import useIsMobile from 'hooks/useIsMobile';
import React, { useCallback, useState } from 'react';
import { FormattedMessage } from 'intl';
import { CommonTranslationKeys } from 'intl/keys';

import { useCurrentUserInProjectLabelled } from 'store/user';
import { PermissionTranslationKeys } from 'shared/domain/permissions/translationKeys';
import { LanguageSwitch } from '../../../../intl/LanguageSwitch';
import { useIsOffline } from '../../../common/withIsOffline';
import { ButtonContained } from '../../../general';
import ForbiddenTooltip from '../../../general/ForbiddenTooltip/ForbiddenTooltip';
import GDPRClauseLink from '../../GDPRClause/GDPRClauseLink';
import CurrentProjectButton from '../CurrentProjectButton/CurrentProjectButton';
import { MENU_BAR_HEADER_HEIGHT_PX } from '../styles';
import { styles } from './styles';

const HeaderInfo = (): React.ReactElement | null => {
  const isMobile = useIsMobile();
  const classes = styles({ isMobile });

  const [drawerOpen, setDrawerOpen] = useState(false);
  const user = useCurrentUserInProjectLabelled();

  const { loading, logout } = useLogout();

  const toggleDrawerOpen = (): void => {
    setDrawerOpen(true);
  };

  const toggleDrawerClose = (): void => {
    setDrawerOpen(false);
  };

  const onLogoutClick = (e: React.MouseEvent): void => {
    e.preventDefault();
    logout();
  };

  if (loading) {
    return null;
  }
  const userName = user.label;

  return (
    <Box
      sx={{
        display: 'flex',
        overflowX: 'auto',
        flexGrow: 1,
        justifyContent: 'flex-end',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          height: MENU_BAR_HEADER_HEIGHT_PX,
          alignItems: 'center',
          flex: '1 1 200px',
          justifyContent: 'flex-end',
          overflow: 'hidden',
        }}
      >
        {isMobile && (
          <Divider
            orientation='vertical'
            sx={{ backgroundColor: 'rgba(37, 37, 37, 0.1)' }}
          />
        )}
        <CurrentProjectButton />
        <Divider
          orientation='vertical'
          sx={{ backgroundColor: 'rgba(37, 37, 37, 0.1)' }}
        />
        <Tooltip title={userName}>
          <Button
            onClick={toggleDrawerOpen}
            data-qa='app-header-user-button'
            sx={{
              mx: { xs: 0, md: 1 },
              maxWidth: { lg: '20%' },
            }}
          >
            {userName && (
              <BackgroundLetterAvatar
                userName={userName}
                email={user.email}
              />
            )}
          </Button>
        </Tooltip>
        <Drawer
          anchor='right'
          data-qa='drawer'
          open={drawerOpen}
          classes={{ paper: classes.drawer }}
          style={{ zIndex: 20000 }}
          onClose={toggleDrawerClose}
        >
          <div
            role='presentation'
            className={classes.sideList}
            onClick={toggleDrawerClose}
            onKeyDown={toggleDrawerClose}
          >
            <Typography variant='h5'>{user.label}</Typography>
            <Typography className={classes.email} variant='h4'>
              {user.email}
            </Typography>
            <LanguageSwitch />
            <ButtonContained
              onClick={onLogoutClick}
              className={classes.logOutButton}
              data-qa='user-sidebar-log-out-button'
            >
              <FormattedMessage id={CommonTranslationKeys.logOut} />
            </ButtonContained>
            <br />
            <EditProfileButton />
            <br />
            <br />
            <GDPRClauseLink />
          </div>
        </Drawer>
      </Box>
    </Box>
  );
};

function EditProfileButton(): React.ReactElement {
  const isMobile = useIsMobile();
  const classes = styles({ isMobile });
  const isOffline = useIsOffline();
  const { goToCurrentUser } = useAppNavigation();

  const handleEditProfileClick = useCallback(() => {
    goToCurrentUser();
  }, [goToCurrentUser]);

  return (
    <ForbiddenTooltip
      visibleWhen={isOffline}
      reason={PermissionTranslationKeys.OFFLINE_UNAVAILABLE}
      inlined
    >
      <ButtonContained
        className={classes.logOutButton}
        data-qa='user-sidebar-edit-button'
        onClick={handleEditProfileClick}
        disabled={isOffline}
      >
        <FormattedMessage id={CommonTranslationKeys.edit} />
      </ButtonContained>
    </ForbiddenTooltip>
  );
}

export default React.memo(HeaderInfo);
